// <pfs-locale-toggle> — brand-agnostischer Sprach-Umschalter (PFS-708).
//
// Das Locale-Pendant zum <pfs-theme-toggle>: zyklisch per Klick durch die
// *tenant-aktiven* Locales, persistiert die Wahl, und schaltet die aktive Locale
// LIVE — über den i18n-Store (`requestLocale`) → der `<pfs-i18n-catalog>`-Loader
// lädt den Katalog für die neue Locale → alle withI18n-WCs re-rendern OHNE
// Full-Reload. Konsumenten hängen es z.B. in ihren sidebar-header/topbar-Slot.
//
// ⭐ Welche Sprachen wählbar sind, ist TENANT-DATEN, nicht hardcoded: mit
// `data-source-url` zieht der Toggle die aktivierten Locales von pfs-content
// (`GET /api/v1/locales` als list.Page über den Shell-Proxy, z. B.
// `/api/proxy/pfs-content/api/v1/locales?only_enabled=true`, PFS-1550; gelesen
// mit fetchAllPages, bis Total) — verwaltet im
// Platform→Sprachen-Tab. Neue Sprache = Zeile dort, keine Manifest-/Deploy-
// Änderung (dynamisch & erweiterbar per Default). `data-locales` bleibt ein
// STATISCHER Fallback-Seam (Erst-Paint vor dem Fetch, Tests, Hosts ohne Endpoint).
//
// Grenze: pfs-components kennt keine Cookies/keinen Server — Persistenz nur in
// localStorage (wie der Theme-Toggle). Der server-gerenderte erste Paint (html
// lang) ist Host-Sache; hier wird nur die Laufzeit-Locale live umgeschaltet.
//
// Attribute (data-*):
//   data-source-url   pfs-content-Locales-Endpoint (bare; apiFetch prependet den
//                     Mount-Prefix), Antwort list.Page. Gesetzt → tenant-getriebene
//                     Auswahl. Schlägt der Abruf fehl oder ist die Liste leer, bleibt
//                     der statische Fallback — und das wird GELOGGT (PFS-1550: ein
//                     stummes catch hat den 404 im Embed monatelang versteckt).
//   data-locales      Statischer Fallback "code[:Label]" (default "de-DE:DE,en-US:EN").
//   data-storage-key  localStorage-Key (default "pfs-locale").
//   data-default      Initial-Locale ohne gespeicherte Wahl (default erste Locale).

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { ICON_BUTTON_CSS } from "../../styles/icon-button.js";
import { fetchAllPages } from "../../list/fetch-all-pages.js";
import { logger } from "../../utils/debug-logger.js";
import { requestLocale, getLocale } from "../../store/i18n-store.js";

interface LocaleOption {
  code: string;
  label: string;
  name: string;
}

// Ein Item von pfs-content `GET /api/v1/locales` (list.Page; nur die Felder,
// die der Toggle nutzt).
interface LocaleRow {
  Code: string;
  NativeName?: string;
  Enabled?: boolean;
}

const DEFAULT_LOCALES = "de-DE:DE,en-US:EN";

const GLOBE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"/></svg>`;

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: inline-block; }
${ICON_BUTTON_CSS}
.wrap { display: inline-flex; align-items: center; gap: 0.35rem; }
.code { font-size: 0.7rem; font-weight: 600; letter-spacing: 0.03em; color: var(--pfs-color-on-surface-subtle); }
`;

// Kurz-Label aus einem Code ableiten: "de-DE" → "DE" (Sprach-Subtag, uppercase).
function shortLabel(code: string): string {
  return code.split("-")[0].trim().toUpperCase();
}

function parseLocales(raw: string): LocaleOption[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((entry) => {
      const [code, label] = entry.split(":");
      const c = code.trim();
      const l = (label || shortLabel(c)).trim().toUpperCase();
      return { code: c, label: l, name: c };
    });
}

defineComponent({
  tagName: "pfs-locale-toggle",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;

    // Statischer Fallback (Erst-Paint + Hosts ohne Endpoint). Der Fetch
    // (falls data-source-url) ersetzt die Liste, sobald er durch ist.
    let locales = parseLocales(host.getAttribute("data-locales") || DEFAULT_LOCALES);
    if (locales.length === 0) return;
    const storageKey = host.getAttribute("data-storage-key") || "pfs-locale";
    const fallback = host.getAttribute("data-default") || locales[0].code;
    const sourceUrl = host.getAttribute("data-source-url");

    const known = (code: string): boolean => locales.some((l) => l.code === code);
    const readLocale = (): string => {
      const saved = localStorage.getItem(storageKey);
      if (saved && known(saved)) return saved;
      const active = getLocale();
      if (active && known(active)) return active;
      return known(fallback) ? fallback : locales[0].code;
    };

    let current = readLocale();

    const style = document.createElement("style");
    style.textContent = STYLES;
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("part", "button");
    const wrap = document.createElement("span");
    wrap.className = "wrap";
    const codeEl = document.createElement("span");
    codeEl.className = "code";
    wrap.innerHTML = GLOBE;
    wrap.appendChild(codeEl);
    button.appendChild(wrap);
    root.replaceChildren(style, button);

    const paint = (): void => {
      const opt = locales.find((l) => l.code === current) ?? locales[0];
      codeEl.textContent = opt.label;
      const label = `Language: ${opt.name} (click to switch)`;
      button.setAttribute("aria-label", label);
      button.setAttribute("title", label);
    };

    const apply = (): void => {
      requestLocale(current);
      paint();
    };

    // Nur EINE aktive Locale → der Umschalter hätte nichts zu tun. Trotzdem
    // sichtbar lassen wäre irreführend; verstecken (progressive Disclosure).
    const reflectVisibility = (): void => {
      host.style.display = locales.length > 1 ? "" : "none";
    };

    button.addEventListener("click", () => {
      if (locales.length < 2) return;
      const idx = locales.findIndex((l) => l.code === current);
      current = locales[(idx + 1) % locales.length].code;
      localStorage.setItem(storageKey, current);
      apply();
    });

    // Erst-Paint (statischer Fallback). Wenn die gespeicherte Locale von der
    // aktuell aktiven abweicht, die Wahl live durchsetzen; sonst nur anzeigen.
    paint();
    reflectVisibility();
    if (getLocale() !== current) apply();

    // Tenant-getriebene Auswahl: die aktivierten Locales von pfs-content ziehen
    // (REST via BFF-Proxy). Ersetzt die statische Liste; behält die aktuelle
    // Wahl, falls sie weiter aktiv ist, sonst fällt sie auf die erste zurück.
    if (sourceUrl) {
      void fetchAllPages<LocaleRow>(sourceUrl)
        .then((rows) => {
          const next = rows
            .filter((r) => r.Code && r.Enabled !== false)
            .map((r) => ({
              code: r.Code,
              label: shortLabel(r.Code),
              name: r.NativeName || r.Code,
            }));
          if (next.length === 0) {
            logger.warn("pfs-locale-toggle: no enabled locale from data-source-url — static data-locales stay", { sourceUrl });
            return;
          }
          locales = next;
          if (!known(current)) current = readLocale();
          reflectVisibility();
          paint();
        })
        .catch((err: unknown) => {
          // Endpoint fehlt/Fehler → statischer Fallback bleibt aktiv, aber nie stumm.
          logger.warn("pfs-locale-toggle: locales not loaded — static data-locales stay", { sourceUrl, err });
        });
    }
  },
});
